import React from 'react';
import Text, { TextType } from '../../../Text/Text';
import {
  SelectedAddons as SelectedAddonsType,
  SlotService,
} from '../../../../../utils/state/types';
import { PriceUtils } from '@wix/bookings-uou-mappers';
import { useFormComponentContext } from '../../../Hooks/useFormComponentContext';
import { TagName } from '../../../../../utils/tagName.const';
import { SingleSlotDetailsDataHook } from './dataHooks';
import { classes, st } from './SelectedAddons.st.css';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { aggregateParticipantAddons } from '../../../../../utils/payment/modifiers/aggregateParticipantAddons';

export type SelectedAddonsProps = {
  slotService: SlotService;
  // Render this selection instead of the slot's (one participant's add-ons).
  selectedAddons?: SelectedAddonsType;
  dataHook?: string;
};

export const SelectedAddons: React.FC<SelectedAddonsProps> = ({
  slotService,
  selectedAddons: selectedAddonsOverride,
  dataHook = SingleSlotDetailsDataHook.SELECTED_ADDONS,
}) => {
  const { dateRegionalSettingsLocale } = useFormComponentContext();
  const { t } = useTranslation();
  const { addOnByGroup, selectedAddons, paymentDetails, participants } =
    slotService;
  const { isMobile } = useEnvironment();

  // Several participants: each one's add-ons are listed in the participants block, so the
  // aggregate is not shown (unless a specific selection was passed in).
  if (!selectedAddonsOverride && (participants?.length ?? 0) > 1) {
    return null;
  }
  const effectiveSelectedAddons =
    selectedAddonsOverride ??
    (participants?.length
      ? aggregateParticipantAddons(participants)
      : selectedAddons);

  if (
    !addOnByGroup ||
    !effectiveSelectedAddons ||
    Object.keys(effectiveSelectedAddons).length === 0
  ) {
    return null;
  }

  const groupedAddons = addOnByGroup.reduce((acc, group) => {
    if (!group.addOns) {
      return acc;
    }

    const selectedAddonsInGroup = group.addOns.filter(
      (addon) =>
        addon.id &&
        effectiveSelectedAddons[group.id!] &&
        effectiveSelectedAddons[group.id!][addon.id] &&
        addon.price,
    );

    if (selectedAddonsInGroup.length > 0) {
      acc.push({
        groupName: group.name!,
        groupId: group.id!,
        addons: selectedAddonsInGroup,
      });
    }

    return acc;
  }, [] as { groupName: string; groupId: string; addons: (typeof addOnByGroup)[0]['addOns'] }[]);

  return (
    <div data-hook={dataHook} className={st(classes.root, { isMobile })}>
      {groupedAddons.map((group, groupIndex) => (
        <div key={groupIndex} className={classes.group}>
          <Text
            type={TextType.Secondary}
            className={classes.groupName}
            tagName={TagName.Span}
          >
            {group.groupName}
          </Text>
          <div className={classes.addons}>
            {group.addons?.map((addon) => {
              if (!addon.id) {
                return null;
              }

              const addonSelection =
                effectiveSelectedAddons[group.groupId][addon.id];
              const quantity = addonSelection.quantity || 1;
              const price = Number(addon.price);
              const currency = addon.currency || paymentDetails.currency;
              const formattedPrice = PriceUtils.getFormattedCurrency({
                price,
                currency,
                locale: dateRegionalSettingsLocale,
              });

              return (
                <div
                  key={addon.id}
                  className={classes.addon}
                  data-hook={SingleSlotDetailsDataHook.SELECTED_ADDON_ITEM}
                >
                  <Text type={TextType.Secondary} tagName={TagName.Paragraph}>
                    {t('app.bookings-form.bookings-details.addons.title', {
                      quantity,
                      name: addon.name,
                      price: price > 0 ? formattedPrice : undefined,
                    })}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
